import {
  AfterContentInit,
  AfterViewInit, ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  HostBinding,
  HostListener, Injector,
  Input,
  QueryList,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Instance, createPopper } from "@popperjs/core";

import { MedOptionType } from "./types";
import { STRING } from "../../constants/string";
import { FieldCoreComponent } from "./FieldCoreComponent";
import { SELECTOR_EMPTY_MESSAGE } from "../../constants/general";
import { MedTemplateDirective } from "../../directives/med-template.directive";
import { KeyNames } from "../../constants/key-names";

const POPOVER_VISIBILITY_ATTR = "data-show";

@Component({
  template: "",
})
export abstract class SelectCoreComponent<OptionValueType> extends FieldCoreComponent<OptionValueType> implements AfterContentInit, AfterViewInit {
  constructor(
    protected override injector: Injector,
    protected override cd: ChangeDetectorRef,
    protected component: ElementRef,
  ) {
    super(injector, cd);
  }

  popperInstance!: Instance;
  optionTemplate?: TemplateRef<any>;
  headerTemplate?: TemplateRef<any>;

  @Input() options: MedOptionType<any>[] = [];
  @Input() emptyMessage: string = SELECTOR_EMPTY_MESSAGE;

  @ViewChild("elRef") elRef!: ElementRef<HTMLElement>;
  @ViewChild("buttonRef") buttonRef!: ElementRef<HTMLElement>;
  @ViewChild("popoverRef") popoverRef!: ElementRef<HTMLElement>;

  @ContentChildren(MedTemplateDirective) templates!: QueryList<MedTemplateDirective>;

  abstract itemSelector: string;
  /*
  Enable ability to focus root(host) element when an user use tab/shift-tab navigation.
  If use keyup events on the root med-label navigation will has strange behaviour focusing on the nested components
   */
  @HostBinding("attr.tabindex") readOnly = "0";

  get hasValue(): boolean {
    return Boolean(Array.isArray(this.value) ? this.value.length : this.value);
  }

  get overlayVisible(): boolean {
    return this.popoverRef?.nativeElement?.hasAttribute(POPOVER_VISIBILITY_ATTR);
  }

  override get classList(): string {
    const extend = this.overlayVisible ? "extend" : STRING.EMPTY;
    const placeholder = !this.value ? "placeholder" : STRING.EMPTY;
    return [this.state, this.validationStateClass, this.size, this.inputClass, this.availabilityStateClass, extend, placeholder].join(STRING.SPACE);
  }

  /*
   When the user clicks the up or down arrow button,
   this function finds the next/previous item depending on the keyCode and focuses on it.
   */
  abstract focusNextActiveOption(event: KeyboardEvent): void;
  abstract findNextActiveOption(item: any, event: KeyboardEvent): HTMLElement | null;

  onToggle(visible: boolean, event?: FocusEvent)  {
    if (this.disabled) {
      return;
    }

    this._updatePopper(visible, event);
  }

  @HostListener("keyup.tab")
  @HostListener("keyup.shift.tab")
  displayPopupOnTabUp(): void {
    this.onToggle(true);
  }

  @HostListener("keydown.tab")
  hidePopupOnTabDown(): void {
    this.onToggle(false);
  }

  /*
  This function hide a dropdown only if a root element 'med-label' loose focus, not his child.
  For example if an use click nested checkbox -> this blur event bubbles to parent.
  So this function prevent such edge cases
  */
  @HostListener("blur", ["$event"])
  handleRootBlur(event?: any) {
    if (!event) {
      return;
    }

    //Workaround. Add ability to focus nested elements. When one of them loses - hide dropdown
    if (this.isNestedElementFocused(event)) {
      this.listenNestedElementBlur(event);
      return;
    }

    this.onToggle(false);
  }

  @HostListener("keyup.esc", ["$event"])
  hidePopupOnEsc(event: any): void {
    //This if allows to correctly work with dialog.
    // If a dialog has an opened select - a first esc click will close select and prevent close dialog.
    // Then when focused select has a hidden popup - esc can propagate up to the dialog
    if (this.overlayVisible) {
      event.stopImmediatePropagation();
    }

    this.onToggle(false);
  }

  @HostListener("keydown.arrowdown", ["$event"])
  @HostListener("keydown.arrowup", ["$event"])
  focusFirstOptionOnArrowKey(event: any): void {
    event.preventDefault();
    const firstOption = this.component.nativeElement.querySelector(this.itemSelector);
    firstOption?.focus();
  }

  onOptionKeydown(event: any) {
    if (event.key === KeyNames.TAB && event.shiftKey) {
      this.component.nativeElement.focus();
      return;
    }

    if (event.key === KeyNames.ENTER) {
      event.target.click();
      event.preventDefault();
      return;
    }

    if (event.key === KeyNames.ARROW_UP || event.key === KeyNames.ARROW_DOWN) {
      this.focusNextActiveOption(event);
      event.preventDefault();
      event.stopPropagation();
    }
  }

  ngAfterContentInit() {
    this.templates.forEach(item => {
      switch (item.getType()) {
      case "header":
        this.headerTemplate = item.template;
        break;
      default:
        this.optionTemplate = item.template;
      }
    });
  }

  override ngAfterViewInit() {
    super.ngAfterViewInit();
    this.popperInstance = createPopper(this.buttonRef.nativeElement, this.popoverRef.nativeElement, {
      placement: "bottom",
      strategy: "absolute",
    });
  }

  protected isNestedElementFocused({ relatedTarget }: any = {}): boolean {
    return relatedTarget
      ? this.component.nativeElement.contains(relatedTarget)
      : false;
  }

  private _updatePopper(enabled: boolean, event?: FocusEvent) {
    // Enable the event listeners
    this.popperInstance?.setOptions((options: any) => ({
      ...options,
      modifiers: [
        ...(options.modifiers || []),
        { name: "eventListeners", enabled },
      ],
    }));

    if (enabled) {
      this.emitFocus(event);
      this.popoverRef.nativeElement.setAttribute(POPOVER_VISIBILITY_ATTR, "");
    } else {
      this._blurSelectControl();
      this.popoverRef.nativeElement.removeAttribute(POPOVER_VISIBILITY_ATTR);
    }

    this.popperInstance?.update();
  }

  private _blurSelectControl(): void {
    if (!this.overlayVisible) {
      return;
    }

    this.onBlur.emit();
    this.elRef.nativeElement.blur();
  }

  private listenNestedElementBlur(event: any): void {
    event.relatedTarget.addEventListener(
      "blur",
      (nestedEvent: any) => this.onNestedElementBlur(nestedEvent),
      { once: true },
    );
  }

  private onNestedElementBlur(event: any) {
    if (!event.relatedTarget) {
      return;
    }

    if (event.relatedTarget.isEqualNode(this.component.nativeElement)) {
      return;
    }

    if (this.isNestedElementFocused(event)) {
      this.listenNestedElementBlur(event);
      return;
    }

    this.onToggle(false);
  }
}
